// components/entities/useLeadDependencyHydration.ts
'use client';

import { useCallback, useEffect, useState } from 'react';
import { useKernel } from '@/kernel/Kernel';

/**
 * Hydrates the Brands, Properties and PortfolioFits referenced by the currently visible leads.
 * Returns the callback to hand to `onVisibleItemIdsChange` on a Lead table or grid.
 */
export const useLeadDependencyHydration = (): ((ids: string[]) => void) => {
  const { kernel } = useKernel();
  const [visibleLeadIdsSignature, setVisibleLeadIdsSignature] = useState('');

  const handleVisibleIdsChange = useCallback((ids: string[]) => {
    setVisibleLeadIdsSignature(Array.isArray(ids) ? ids.join('|') : '');
  }, []);

  useEffect(() => {
    const k = kernel.current;
    if (!k || visibleLeadIdsSignature.length === 0) return;
    const visibleIds = visibleLeadIdsSignature.split('|').filter((id) => id.length > 0);
    if (visibleIds.length === 0) return;

    const inv = k.getInventory();
    const leadReg = inv.Lead.registry;
    const brandReg = inv.Brand.registry;
    const propReg = inv.Property.registry;
    const portfolioFitReg = inv.PortfolioFit.registry;

    const brandIdsToFetch = new Set<string>();
    const propIdsToFetch = new Set<string>();
    const portfolioFitIdsToFetch = new Set<string>();

    visibleIds.forEach((id) => {
      const lead = leadReg[id];
      if (!lead) return;
      if (lead.brandId && !brandReg[lead.brandId]) brandIdsToFetch.add(lead.brandId);
      if (lead.propertyId && !propReg[lead.propertyId]) propIdsToFetch.add(lead.propertyId);
      if (lead.portfolioFitId && !portfolioFitReg[lead.portfolioFitId]) {
        portfolioFitIdsToFetch.add(lead.portfolioFitId);
      }
    });

    const sortedBrandIds = Array.from(brandIdsToFetch).sort();
    const sortedPropertyIds = Array.from(propIdsToFetch).sort();
    const sortedPortfolioFitIds = Array.from(portfolioFitIdsToFetch).sort();

    if (sortedBrandIds.length > 0) {
      void k.ensureInventoryIds('Brand', sortedBrandIds, { force: false });
    }
    if (sortedPropertyIds.length > 0) {
      void k.ensureInventoryIds('Property', sortedPropertyIds, { force: false });
    }
    if (sortedPortfolioFitIds.length > 0) {
      void k.ensureInventoryIds('PortfolioFit', sortedPortfolioFitIds, { force: false });
    }
  }, [kernel, visibleLeadIdsSignature]);

  return handleVisibleIdsChange;
};
